import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Users, Clock, CalendarDays, ChevronDown, ChevronUp } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from 'config/firebase.config';
import { calculateWorkingDays, getNSWHolidays } from 'utils/utils';

export const AnnualLeaveReport = () => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [useCustomRange, setUseCustomRange] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed by default

    // Set default dates (last month)
    useEffect(() => {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        
        setStartDate(lastMonth.toISOString().split('T')[0]);
        setEndDate(lastMonthEnd.toISOString().split('T')[0]);
    }, []);

    const generateReport = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Fetch public holidays first
            const publicHolidays = await getNSWHolidays();
            
            // Use custom dates if custom range is selected, otherwise use last month
            let reportStartDate, reportEndDate;
            
            if (useCustomRange) {
                if (!startDate || !endDate) {
                    setError('Please select both start and end dates.');
                    setLoading(false);
                    return;
                }
                reportStartDate = new Date(startDate);
                reportEndDate = new Date(endDate);
            } else {
                const now = new Date();
                reportStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                reportEndDate = new Date(now.getFullYear(), now.getMonth(), 0);
            }
            
            console.log(`Generating report for: ${reportStartDate.toISOString().split('T')[0]} to ${reportEndDate.toISOString().split('T')[0]}`);
            
            // Query leave requests
            const leaveRequestsRef = collection(db, 'leaveRequests');
            const snapshot = await getDocs(leaveRequestsRef);
            
            if (snapshot.empty) {
                setReportData({
                    totalRequests: 0,
                    annualRequests: 0,
                    totalDays: 0,
                    byEmployee: {},
                    requests: []
                });
                setLoading(false);
                return;
            }
            
            let totalRequests = 0;
            let annualLeaveRequests = [];
            let totalAnnualLeaveDays = 0;
            
            // First, collect all annual leave requests
            const allAnnualRequests = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                totalRequests++;
                
                // Check if it's an annual leave request
                if (data.leaveType === 'annual') {
                    // Check if the request was created in the selected date range
                    const createdAt = data.createdAt ? data.createdAt.toDate() : null;
                    const isInDateRange = createdAt && 
                        createdAt >= reportStartDate && 
                        createdAt <= reportEndDate;
                    
                    if (isInDateRange) {
                        allAnnualRequests.push({
                            employeeName: data.employeeName,
                            startDate: data.startDate,
                            endDate: data.endDate,
                            status: data.status,
                            createdAt: createdAt.toISOString().split('T')[0],
                            requestId: doc.id
                        });
                    }
                }
            });

            // Deduplicate requests by employee and date range
            const uniqueRequests = new Map();
            const duplicateRequests = [];
            
            allAnnualRequests.forEach(request => {
                const key = `${request.employeeName}-${request.startDate}-${request.endDate}`;
                
                if (!uniqueRequests.has(key)) {
                    // First time seeing this employee-date combination
                    uniqueRequests.set(key, request);
                } else {
                    // Duplicate found - check which is more recent
                    const existingRequest = uniqueRequests.get(key);
                    if (new Date(request.createdAt) > new Date(existingRequest.createdAt)) {
                        // Current request is more recent, move existing to duplicates
                        duplicateRequests.push({
                            ...existingRequest,
                            reason: 'Replaced by newer request',
                            replacedBy: request.createdAt
                        });
                        uniqueRequests.set(key, request);
                    } else {
                        // Existing request is more recent, add current to duplicates
                        duplicateRequests.push({
                            ...request,
                            reason: 'Older duplicate request',
                            replacedBy: existingRequest.createdAt
                        });
                    }
                }
            });

            // Convert to array and calculate days (excluding weekends and public holidays)
            annualLeaveRequests = Array.from(uniqueRequests.values()).map(request => {
                const days = calculateWorkingDays(request.startDate, request.endDate, publicHolidays);
                
                return {
                    ...request,
                    days: days
                };
            });

            // Calculate total days
            totalAnnualLeaveDays = annualLeaveRequests.reduce((sum, request) => sum + request.days, 0);
            
            // Group by employee
            const byEmployee = {};
            annualLeaveRequests.forEach(request => {
                if (!byEmployee[request.employeeName]) {
                    byEmployee[request.employeeName] = {
                        totalDays: 0,
                        requests: 0
                    };
                }
                byEmployee[request.employeeName].totalDays += request.days;
                byEmployee[request.employeeName].requests += 1;
            });
            
            setReportData({
                totalRequests,
                annualRequests: annualLeaveRequests.length,
                totalDays: totalAnnualLeaveDays,
                byEmployee,
                requests: annualLeaveRequests,
                duplicateRequests: duplicateRequests,
                dateRange: {
                    start: reportStartDate.toISOString().split('T')[0],
                    end: reportEndDate.toISOString().split('T')[0]
                }
            });
            
        } catch (err) {
            console.error('Error generating report:', err);
            setError('Failed to generate report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        generateReport();
    }, [useCustomRange, startDate, endDate]);

    const handleDateRangeChange = () => {
        generateReport();
    };

    const resetToLastMonth = () => {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        
        setStartDate(lastMonth.toISOString().split('T')[0]);
        setEndDate(lastMonthEnd.toISOString().split('T')[0]);
        setUseCustomRange(false);
    };

    // Show loading/error states even when collapsed
    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Calendar className="h-6 w-6 mr-2 text-blue-600" />
                        Annual Leave Report
                    </h2>
                </div>
                <div className="flex items-center justify-center mt-4">
                    <Clock className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                    <span>Generating annual leave report...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Calendar className="h-6 w-6 mr-2 text-blue-600" />
                        Annual Leave Report
                    </h2>
                </div>
                <div className="text-red-600 text-center mt-4">
                    <p>{error}</p>
                    <button 
                        onClick={generateReport}
                        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!reportData) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Calendar className="h-6 w-6 mr-2 text-blue-600" />
                        Annual Leave Report
                        <button 
                            className="ml-3 text-gray-500 hover:text-gray-700"
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            title={isCollapsed ? "Expand report" : "Collapse report"}
                        >
                            {isCollapsed ? (
                                <ChevronDown className="h-5 w-5" />
                            ) : (
                                <ChevronUp className="h-5 w-5" />
                            )}
                        </button>
                    </h2>
                    <div className="flex items-center space-x-2">
                        <button 
                            onClick={resetToLastMonth}
                            className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                        >
                            Last Month
                        </button>
                        <button 
                            onClick={generateReport}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Refresh
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md p-6" >
            <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                    <Calendar className="h-6 w-6 mr-2 text-blue-600" />
                    Annual Leave Report
                    <button 
                        className="ml-3 text-gray-500 hover:text-gray-700"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        title={isCollapsed ? "Expand report" : "Collapse report"}
                    >
                        {isCollapsed ? (
                            <ChevronDown className="h-5 w-5" />
                        ) : (
                            <ChevronUp className="h-5 w-5" />
                        )}
                    </button>
                </h2>
                <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                    <button 
                        onClick={resetToLastMonth}
                        className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                    >
                        Last Month
                    </button>
                    <button 
                        onClick={generateReport}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {!isCollapsed && (
                <div className="mt-6">
                    {/* Date Range Selection */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                        <CalendarDays className="h-5 w-5 mr-2 text-gray-600" />
                        Select Date Range
                    </h3>
                    <div className="flex items-center space-x-2">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                name="dateRange"
                                checked={!useCustomRange}
                                onChange={() => setUseCustomRange(false)}
                                className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Last Month</span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                name="dateRange"
                                checked={useCustomRange}
                                onChange={() => setUseCustomRange(true)}
                                className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Custom Range</span>
                        </label>
                    </div>
                </div>

                {useCustomRange && (
                    <div className="space-y-4">
                        {/* Quick Date Range Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                                    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
                                    setStartDate(lastMonth.toISOString().split('T')[0]);
                                    setEndDate(lastMonthEnd.toISOString().split('T')[0]);
                                }}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                            >
                                Last Month
                            </button>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                                    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                                    setStartDate(thisMonth.toISOString().split('T')[0]);
                                    setEndDate(thisMonthEnd.toISOString().split('T')[0]);
                                }}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                            >
                                This Month
                            </button>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const last3Months = new Date(now.getFullYear(), now.getMonth() - 3, 1);
                                    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
                                    setStartDate(last3Months.toISOString().split('T')[0]);
                                    setEndDate(lastMonthEnd.toISOString().split('T')[0]);
                                }}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                            >
                                Last 3 Months
                            </button>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const thisYear = new Date(now.getFullYear(), 0, 1);
                                    const thisYearEnd = new Date(now.getFullYear(), 11, 31);
                                    setStartDate(thisYear.toISOString().split('T')[0]);
                                    setEndDate(thisYearEnd.toISOString().split('T')[0]);
                                }}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                            >
                                This Year
                            </button>
                        </div>

                        {/* Manual Date Selection */}
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700">From:</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700">To:</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <button
                                onClick={handleDateRangeChange}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                            >
                                Apply Range
                            </button>
                        </div>
                    </div>
                )}

                <div className="mt-3">
                    <p className="text-gray-600">
                        <strong>Current Range:</strong> {reportData.dateRange.start} to {reportData.dateRange.end}
                    </p>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 p-6 rounded-lg">
                    <div className="flex items-center">
                        <TrendingUp className="h-8 w-8 text-blue-600 mr-3" />
                        <div>
                            <p className="text-sm font-medium text-blue-600">Total Leave Requests</p>
                            <p className="text-2xl font-bold text-blue-900">{reportData.totalRequests}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                    <div className="flex items-center">
                        <Calendar className="h-8 w-8 text-green-600 mr-3" />
                        <div>
                            <p className="text-sm font-medium text-green-600">Annual Leave Requests</p>
                            <p className="text-2xl font-bold text-green-900">{reportData.annualRequests}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                    <div className="flex items-center">
                        <Clock className="h-8 w-8 text-purple-600 mr-3" />
                        <div>
                            <p className="text-sm font-medium text-purple-600">Total Annual Leave Days</p>
                            <p className="text-2xl font-bold text-purple-900">{reportData.totalDays}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Duplicate Requests Section */}
            {reportData.duplicateRequests && reportData.duplicateRequests.length > 0 && (
                <div className="mb-6" style={{ display: 'none' }}>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <svg className="h-5 w-5 mr-2 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Duplicate Leave Requests ({reportData.duplicateRequests.length})
                    </h3>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <p className="text-sm text-orange-800 mb-4">
                            The following duplicate leave requests were found and excluded from the main report:
                        </p>
                        <div className="space-y-3">
                            {reportData.duplicateRequests.map((duplicate, index) => {
                                // Note: We don't have holidays in this context, but duplicates are hidden anyway
                                const days = calculateWorkingDays(duplicate.startDate, duplicate.endDate);
                                
                                return (
                                    <div key={index} className="bg-white p-3 rounded border border-orange-200">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2">
                                                    <span className="font-semibold text-gray-800">{duplicate.employeeName}</span>
                                                    <span className="text-sm text-gray-500">•</span>
                                                    <span className="text-sm text-gray-600">
                                                        {duplicate.startDate} to {duplicate.endDate}
                                                    </span>
                                                    <span className="text-sm text-gray-500">•</span>
                                                    <span className="text-sm font-medium text-blue-600">{days} days</span>
                                                </div>
                                                <div className="mt-1 text-xs text-gray-500">
                                                    Requested: {duplicate.createdAt} • Status: {duplicate.status}
                                                </div>
                                            </div>
                                            <div className="ml-4 text-right">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    duplicate.reason === 'Replaced by newer request' 
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {duplicate.reason}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Employee Breakdown */}
            {Object.keys(reportData.byEmployee).length > 0 ? (
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <Users className="h-5 w-5 mr-2 text-gray-600" />
                        Annual Leave by Employee
                    </h3>
                    <div className="space-y-4">
                        {Object.entries(reportData.byEmployee).map(([employee, data]) => (
                            <div key={employee} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-center">
                                    <h4 className="font-semibold text-gray-800">{employee}</h4>
                                    <div className="text-right">
                                        <p className="text-lg font-bold text-blue-600">{data.totalDays} days</p>
                                        <p className="text-sm text-gray-500">{data.requests} request{data.requests > 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                                
                                {/* Individual requests for this employee */}
                                <div className="mt-3 space-y-2">
                                    {reportData.requests
                                        .filter(req => req.employeeName === employee)
                                        .map((request, index) => (
                                            <div key={index} className="bg-gray-50 p-3 rounded text-sm">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600">
                                                        {request.startDate} to {request.endDate}
                                                    </span>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="font-medium">{request.days} days</span>
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                            request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-red-100 text-red-800'
                                                        }`}>
                                                            {request.status}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Requested: {request.createdAt}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No annual leave requests found for last month.</p>
                </div>
            )}
                </div>
            )}
        </div>
    );
};
